import { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { X } from "@phosphor-icons/react";

// Accessible dialog shell: fades the backdrop in, springs the panel up,
// locks page scroll, closes on Escape / backdrop click when onClose is given.
const Modal = ({ onClose, labelledBy, width = 460, children }) => {
  const panelRef = useRef(null);

  useEffect(() => {
    document.body.classList.add("no-scroll");
    const previouslyFocused = document.activeElement;
    panelRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape" && onClose) onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.classList.remove("no-scroll");
      document.removeEventListener("keydown", onKeyDown);
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, [onClose]);

  return (
    <Backdrop onMouseDown={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}>
      <Panel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        style={{ "--w": `${width}px` }}
      >
        {onClose && (
          <Close type="button" onClick={onClose} aria-label="Close">
            <X size={20} weight="bold" />
          </Close>
        )}
        {children}
      </Panel>
    </Backdrop>
  );
};

const fade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const rise = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to { opacity: 1; transform: none; }
`;

const sheet = keyframes`
  from { transform: translateY(100%); }
  to { transform: none; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: rgb(18 23 46 / 0.6);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  animation: ${fade} 0.3s ease both;
  overflow-y: auto;

  @media (max-width: 560px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const Panel = styled.div`
  position: relative;
  width: 100%;
  max-width: var(--w);
  max-height: calc(100dvh - 48px);
  overflow-y: auto;
  padding: clamp(24px, 4vw, 36px);
  border-radius: 24px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-lg);
  animation: ${rise} 0.55s var(--ease-out) both;
  outline: none;

  @media (max-width: 560px) {
    max-width: none;
    max-height: 92dvh;
    border-radius: 24px 24px 0 0;
    padding-bottom: calc(28px + env(safe-area-inset-bottom));
    animation: ${sheet} 0.5s var(--ease-out) both;
  }
`;

const Close = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  transition:
    transform 0.3s var(--ease-out),
    border-color 0.2s ease;

  &:hover {
    transform: rotate(90deg);
    border-color: var(--text);
  }
`;

export default Modal;
